import { LngLat, Marker } from "mapbox-gl";

interface EventWithLngLat extends Event {
    lngLat: mapboxgl.PointLike;
}

export class MapboxMapMarker extends HTMLElement {
    _lngLat!: LngLat;
    _draggable: boolean = false;
    _marker: Marker | undefined;
    _isDragging: boolean = false;
    _eventListenerMap: Map<string, EventListener> = new Map();

    get lngLat(): LngLat {
        return this._lngLat;
    }

    set lngLat(value: LngLat) {
        if (this._marker && !this._isDragging) {
            this._marker.setLngLat(value);
        }
        this._lngLat = value;
    }

    get draggable(): boolean {
        return this._draggable;
    }

    set draggable(value: boolean) {
        if (this._marker) {
            this._marker.setDraggable(value);
        }
        this._draggable = value;
    }

    connectedCallback() {
        console.debug("Connecting elm-mapbox-marker");
        let map = (this.parentElement as any)._map;
        if (!map) {
            console.warn(
                "elm-mapbox-marker element must be a child of an elm-mapbox-map",
            );
            return;
        }

        const element = document.createElement("div");
        element.replaceChildren(
            ...this.childNodes.values().map((node) => node.cloneNode(true)),
        );

        this._marker = new Marker({ element: element })
            .setLngLat(this._lngLat)
            .setDraggable(this._draggable)
            .addTo(map);

        this._marker.on("dragstart", () => {
            this._isDragging = true;
        });
        this._marker.on("dragend", () => {
            this._isDragging = false;
        });

        for (let [type, fn] of this._eventListenerMap) {
            this.addEventListener(type, fn);
        }

        // observe child element changes to update marker element
        const observer = new MutationObserver(() => {
            if (this._marker) {
                this._marker
                    .getElement()
                    .replaceChildren(
                        ...this.childNodes
                            .values()
                            .map((node) => node.cloneNode(true)),
                    );
            }
            // TODO: reattach event listeners?
        });
        observer.observe(this, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });
    }

    addEventListener(type: string, fn: (event: any) => void, ...args: any[]) {
        if (this._marker) {
            if (["dragstart", "drag", "dragend"].includes(type)) {
                this._marker.on(type, (e) => {
                    const lngLat = [
                        e.target.getLngLat().lng,
                        e.target.getLngLat().lat,
                    ];
                    return fn({
                        ...e,
                        lngLat,
                    });
                });
            } else {
                this._marker.getElement().addEventListener(type, (e) => {
                    e.stopPropagation();
                    fn(e);
                });
            }
        }
        this._eventListenerMap.set(type, fn);
    }

    removeEventListener(
        type: any,
        fn: (this: HTMLElement, ev: any) => any,
        ...args: any[]
    ) {
        if (this._marker) {
            this._marker.getElement().removeEventListener(type, fn, ...args);
        }
        this._eventListenerMap.delete(type);
    }

    disconnectedCallback() {
        console.debug("Disconnecting elm-mapbox-marker");
        this._marker?.remove();
        delete this._marker;
    }
}
